import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Check, FilePlus2, GitBranch, Play, TrendingUp } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { PageHeader } from '@/app/shell/PageHeader'
import {
  useAssets,
  useFleetGrid,
  useFleetOps,
  useSetScenario,
  useTelemetry,
  useTelemetryStream,
  type Scenario,
} from '@/lib/api/operations'
import { useCreateObligation } from '@/lib/api/hooks'
import { AreaTrend, Heatmap, ModuleRadar, RealtimeLine, SignalGauge, tok, type TrendPoint } from '@/charts'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { Alert } from '@/ui/alert'
import { ScadaLiveTile } from '@/ui/scada-live-tile'
import { EmptyState, LoadingBlock } from '@/ui/states'
import { cn } from '@/lib/utils'

const CONN: Record<string, [label: string, color: string]> = {
  connecting: ['connecting', tok('--faint')],
  live: ['live · 1s', tok('--clear')],
  stale: ['stale', tok('--flare')],
  error: ['disconnected', tok('--ignition')],
}
const SCENARIOS: { id: Scenario; label: string }[] = [
  { id: 'normal', label: 'Normal' },
  { id: 'degrading', label: 'Degrading' },
  { id: 'incident', label: 'Incident' },
]
// Compact axis labels for the health radar + fleet heatmap (full labels overflow).
const SHORT: Record<string, string> = {
  cell_dt: 'ΔT',
  soc: 'SoC',
  voltage_spread: 'ΔV',
  off_gas: 'Off-gas',
  lel: 'LEL',
  pressure: 'Press',
  vibration: 'Vib',
}

export function OperationsPage() {
  const { siteId } = useCurrentSite()
  const nav = useNavigate()
  const { role } = useRole()
  const canAct = role === 'assessor' || role === 'owner' // full R/W + operator write-on-alert
  const assets = useAssets(siteId)
  const assetList = assets.data ?? []

  // Asset selector: default to the first asset; reset when the site changes.
  const [selId, setSelId] = useState<string>()
  useEffect(() => setSelId(undefined), [siteId])
  const assetId = selId && assetList.some((a) => a.id === selId) ? selId : assetList[0]?.id
  const asset = assetList.find((a) => a.id === assetId)

  const telemetry = useTelemetry(assetId)
  const { tick, status } = useTelemetryStream(assetId)
  const fleet = useFleetOps(siteId)
  const grid = useFleetGrid(siteId)
  const setScenario = useSetScenario()
  const raiseObligation = useCreateObligation(siteId ?? '')

  // Alert handling: acknowledge mutes the callout; raise creates a real obligation
  // (obligations feed the critical-risk flag). Both re-arm when the alert clears.
  const [acked, setAcked] = useState(false)
  const [raised, setRaised] = useState(false)

  // Rolling per-signal series: seed from the snapshot, append each live tick.
  const [series, setSeries] = useState<Record<string, number[]>>({})
  useEffect(() => {
    if (!telemetry.data) return
    const init: Record<string, number[]> = {}
    for (const [k, s] of Object.entries(telemetry.data.signals)) init[k] = [...s.history.map((h) => h.v), s.current]
    setSeries(init)
  }, [telemetry.data])
  useEffect(() => {
    if (!tick) return
    setSeries((prev) => {
      const next = { ...prev }
      for (const [k, cell] of Object.entries(tick.signals)) next[k] = [...(prev[k] ?? []), cell.v].slice(-80)
      return next
    })
  }, [tick])

  const scenario = tick?.scenario ?? telemetry.data?.scenario ?? 'normal'
  const conn = CONN[status] ?? CONN.connecting
  const signalKeys = telemetry.data ? Object.keys(telemetry.data.signals) : []
  const snap = (k: string) => telemetry.data!.signals[k]
  const liveVal = (k: string) => tick?.signals[k]?.v ?? telemetry.data?.signals[k]?.current ?? 0
  const isOver = (k: string) => tick?.signals[k]?.over ?? telemetry.data?.signals[k]?.over ?? false
  // Margin within the operating band: nominal → 100 (full margin), threshold → 0.
  // Measured against (threshold − nominal) so normal operation reads healthy, not alarming.
  const headroom = (k: string) => {
    const s = telemetry.data?.signals[k]
    if (!s) return 100
    const band = s.threshold - s.nominal
    return band > 0 ? Math.max(0, Math.min(100, ((s.threshold - liveVal(k)) / band) * 100)) : 100
  }
  const primaryKey = signalKeys[0]
  const primaryOver = primaryKey ? isOver(primaryKey) : false

  // Trend & projection for the primary signal, fed by the LIVE rolling series (same
  // source as the live chart, so the two stay consistent during an incident). A short
  // linear forecast from the recent slope is drawn dashed — it visibly climbs over the
  // threshold when the signal is ramping.
  const PROJ_STEPS = 14
  const trend = useMemo<TrendPoint[]>(() => {
    if (!primaryKey) return []
    const s = series[primaryKey]
    if (!s || s.length < 3) return []
    const thr = telemetry.data?.signals[primaryKey]?.threshold
    const cap = thr ? thr * 1.5 : Infinity // keep the y-axis readable during a steep ramp
    const win = s.slice(-50)
    const n = win.length
    const pts: TrendPoint[] = win.map((v, i) => ({ t: i, v }))
    const W = Math.min(16, n)
    const slope = (win[n - 1] - win[n - W]) / (W - 1)
    const last = win[n - 1]
    pts.push({ t: n - 1, v: last, proj: last }) // anchor the projection at the last actual
    for (let i = 1; i <= PROJ_STEPS; i++)
      pts.push({ t: n - 1 + i, proj: Math.min(cap, Math.max(0, last + slope * i)) })
    return pts
  }, [primaryKey, series, telemetry.data])
  const projValue = trend.length ? (trend[trend.length - 1].proj ?? 0) : 0

  // Re-arm acknowledge/raise once the signal falls back within limits.
  useEffect(() => {
    if (!primaryOver) {
      setAcked(false)
      setRaised(false)
    }
  }, [primaryOver])

  const onRaise = () => {
    if (!assetId || !primaryKey || raiseObligation.isPending) return
    const s = snap(primaryKey)
    raiseObligation.mutate(
      {
        title: `SCADA alert — ${s.label} threshold crossed on ${asset?.name ?? assetId} (${liveVal(primaryKey)}${s.unit} vs ${s.threshold}${s.unit} limit)`,
        severity: 'high',
      },
      { onSuccess: () => setRaised(true) },
    )
  }

  const header = (
    <PageHeader
      title="Operations — SCADA"
      subtitle={
        <>
          <span>{asset ? asset.name : 'assets'}</span>
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold"
            style={{ color: conn[1], background: `color-mix(in srgb, ${conn[1]} 14%, transparent)` }}
          >
            <span className="size-2 rounded-full" style={{ background: conn[1] }} /> {conn[0]}
          </span>
        </>
      }
      actions={
        <div className="flex items-center gap-2">
          <span className="hidden items-center gap-1.5 rounded-md px-2 py-1 text-xs text-stable sm:inline-flex">
            <GitBranch className="size-3.5" /> feeds the engine
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-input">
            {SCENARIOS.map((s) => (
              <button
                key={s.id}
                onClick={() => assetId && setScenario.mutate({ asset_id: assetId, scenario: s.id })}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold',
                  scenario === s.id
                    ? s.id === 'incident'
                      ? 'bg-ignition/[0.14] text-ignition'
                      : 'bg-secondary text-foreground'
                    : 'text-muted-foreground hover:bg-secondary',
                )}
              >
                {s.id === 'incident' && <Play className="size-3" />}
                {s.label}
              </button>
            ))}
          </div>
        </div>
      }
    />
  )

  if (assets.isLoading || telemetry.isLoading)
    return (
      <>
        {header}
        <Card>
          <LoadingBlock lines={5} />
        </Card>
      </>
    )
  if (!assetId || !telemetry.data)
    return (
      <>
        {header}
        <EmptyState kind="no-data" title="No operational assets">
          This site has no SCADA-instrumented assets yet.
        </EmptyState>
      </>
    )

  // Assets whose live headroom shows a signal at/over its threshold (pill warning dot).
  const alertAssets = new Set(
    (grid.data?.assets ?? [])
      .filter((_, i) => (grid.data!.grid[i] ?? []).some((v) => v != null && v <= 0))
      .map((a) => a.id),
  )
  const radar = signalKeys.map((k) => ({ module: SHORT[k] ?? snap(k).label, value: Math.round(headroom(k)) }))
  // Trend verdict from the live value + client-side forecast (consistent with the live chart).
  const pCur = primaryKey ? liveVal(primaryKey) : 0
  const pThr = primaryKey ? snap(primaryKey).threshold : 0
  const pUnit = primaryKey ? snap(primaryKey).unit : ''
  const trendState = pCur > pThr ? 'over the limit' : projValue > pThr ? 'projected to cross' : 'within limit'
  const trendColor = pCur > pThr ? '--ignition' : projValue > pThr ? '--flare' : '--stable'

  return (
    <>
      {header}

      {assetList.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-faint">
            Asset
          </span>
          {assetList.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelId(a.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12.5px] font-medium transition-colors',
                a.id === assetId
                  ? 'border-primary bg-primary/[0.1] text-foreground'
                  : 'border-input text-muted-foreground hover:bg-secondary',
              )}
            >
              {a.name}
              {alertAssets.has(a.id) && (
                <span className="size-1.5 rounded-full" style={{ background: tok('--flare') }} />
              )}
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {signalKeys.map((k) => (
          <ScadaLiveTile
            key={k}
            label={snap(k).label}
            value={liveVal(k)}
            unit={snap(k).unit}
            spark={series[k]}
            threshold={snap(k).threshold}
            over={isOver(k)}
            color={tok('--stable')}
          />
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.5fr_1fr]">
        <Card>
          <div className="mb-1 flex items-center justify-between">
            <KLabel>{primaryKey ? snap(primaryKey).label : ''} — live</KLabel>
            <span className="text-[11px] text-faint">
              threshold {primaryKey ? snap(primaryKey).threshold : ''} {primaryKey ? snap(primaryKey).unit : ''} ·
              projected → {projValue.toFixed(1)}
            </span>
          </div>
          {primaryKey && (
            <RealtimeLine
              data={series[primaryKey] ?? []}
              threshold={snap(primaryKey).threshold}
              colorToken={primaryOver ? '--flare' : '--stable'}
              height={220}
            />
          )}
        </Card>

        <div className="space-y-4">
          {primaryOver ? (
            <Alert
              tone={acked ? 'warning' : 'critical'}
              title={`Threshold crossed — ${snap(primaryKey).label}${acked ? ' · acknowledged' : ''}`}
              action={
                raised ? (
                  <button
                    onClick={() => nav(`/site/${siteId}/obligations`)}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-stable hover:underline"
                  >
                    <Check className="size-3.5" /> Obligation raised — view →
                  </button>
                ) : canAct ? (
                  <>
                    <Button size="sm" variant="default" onClick={onRaise} disabled={raiseObligation.isPending}>
                      <FilePlus2 /> {raiseObligation.isPending ? 'Raising…' : 'Raise obligation'}
                    </Button>
                    {!acked && (
                      <Button size="sm" variant="outline" onClick={() => setAcked(true)}>
                        <Check /> Acknowledge
                      </Button>
                    )}
                  </>
                ) : undefined
              }
            >
              {liveVal(primaryKey)} {snap(primaryKey).unit} vs {snap(primaryKey).threshold} limit. Rising trend —
              recommend inspection.
            </Alert>
          ) : (
            <Alert tone="success" title="All signals within limits">
              No thresholds crossed on {asset?.name}.
            </Alert>
          )}

          <Card>
            <KLabel className="mb-2 flex items-center gap-1.5">
              <GitBranch className="size-3.5" /> Feeds the engine
            </KLabel>
            <p className="mb-3 text-[12.5px] text-muted-foreground">
              Each live signal maps to the barrier it would inform once the engine feed is enabled.
            </p>
            <div className="space-y-1.5">
              {signalKeys
                .filter((k) => snap(k).feeds)
                .map((k) => (
                  <div key={k} className="flex flex-wrap items-center gap-1.5 text-[12px]">
                    <span className="rounded border bg-surface-2 px-2 py-0.5">{snap(k).label}</span>
                    <ArrowRight className="size-3 text-faint" />
                    <span className="rounded border bg-surface-2 px-2 py-0.5">{snap(k).feeds![0]}</span>
                    <ArrowRight className="size-3 text-faint" />
                    <span className="rounded border border-primary px-2 py-0.5 text-foreground">{snap(k).feeds![1]}</span>
                  </div>
                ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Signal analytics — trend & projection + at-a-glance health profile */}
      {primaryKey && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
          <Card>
            <div className="mb-1 flex items-center justify-between">
              <KLabel className="flex items-center gap-1.5">
                <TrendingUp className="size-3.5" /> {snap(primaryKey).label} — trend &amp; projection
              </KLabel>
              <span className="text-[11px] text-faint">history · dashed = projected</span>
            </div>
            <AreaTrend
              data={trend}
              threshold={snap(primaryKey).threshold}
              color={primaryOver ? tok('--flare') : tok('--stable')}
              height={210}
            />
            <div className="mt-1 text-[11.5px] text-muted-foreground">
              Now {pCur}
              {pUnit} · projected {projValue.toFixed(1)}
              {pUnit} vs {pThr}
              {pUnit} limit —{' '}
              <span style={{ color: tok(trendColor) }} className="font-semibold">
                {trendState}
              </span>
            </div>
          </Card>

          <Card>
            <KLabel className="mb-1">Signal health — headroom profile</KLabel>
            <ModuleRadar data={radar} height={230} />
            <div className="mt-1 text-center text-[11px] text-faint">
              outer ring = full margin · pulled toward centre = near threshold
            </div>
          </Card>
        </div>
      )}

      {/* Per-signal gauges: live value filling toward its limit (tick = threshold) */}
      <Card className="mt-4">
        <div className="mb-1 flex items-center justify-between">
          <KLabel>Signals vs limits</KLabel>
          <span className="text-[11px] text-faint">fill nears the tick as a signal approaches its threshold</span>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {signalKeys.map((k) => (
            <div key={k} className="flex flex-col items-center gap-0.5">
              <SignalGauge value={liveVal(k)} threshold={snap(k).threshold} unit={snap(k).unit} />
              <div className="text-[12.5px] font-semibold">{snap(k).label}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="mb-3 mt-6 font-display text-[13px] font-semibold">Fleet rollup</div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ['Assets streaming', fleet.data?.assets_streaming ?? '—', tok('--stable')],
          ['Alerts active', fleet.data?.alerts_active ?? '—', (fleet.data?.alerts_active ?? 0) > 0 ? tok('--flare') : tok('--stable')],
          ['Stale feeds', fleet.data?.stale_feeds ?? '—', tok('--faint')],
          ['Scenario', scenario, scenario === 'incident' ? tok('--ignition') : tok('--muted-foreground')],
        ].map(([label, value, color]) => (
          <Card key={String(label)}>
            <KLabel>{label}</KLabel>
            <div className="mt-2 font-display text-[24px] font-semibold capitalize tabular" style={{ color: color as string }}>
              {value}
            </div>
          </Card>
        ))}
      </div>

      {grid.data && grid.data.assets.length > 0 && (
        <Card className="mt-4">
          <KLabel className="mb-1">Fleet signal headroom</KLabel>
          <p className="mb-2 text-[12px] text-muted-foreground">
            Each cell is a signal's margin to its threshold — warmer = closer to the limit. Rows are assets on this site.
          </p>
          <Heatmap
            rows={grid.data.assets.map((a) => a.name)}
            cols={grid.data.signals.map((s) => SHORT[s.key] ?? s.label)}
            grid={grid.data.grid.map((row) => row.map((v) => v ?? 0))}
            height={grid.data.assets.length * 44 + 30}
            padL={116}
            showValues
          />
        </Card>
      )}
    </>
  )
}
